import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { ArrowBack, Home } from '@mui/icons-material';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Conditions Générales d'Utilisation
          </Typography>
          <IconButton color="inherit" onClick={() => navigate('/')}>
            <Home />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Conditions Générales d'Utilisation
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          {/* Article 1 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              1. Présentation du service
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le présent service de suivi ANEF (ci-après "le Service") est édité par le{' '}
              <strong>Cyber Mangaza</strong>, situé à Combani, en face de la pharmacie, Mayotte.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Service permet aux utilisateurs de suivre automatiquement l'état de leurs dossiers
              sur la plateforme ANEF (Administration Numérique pour les Étrangers en France) et de
              recevoir des notifications en temps réel concernant les demandes de compléments de dossier.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Contact : WhatsApp +262 639 77 86 00
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 2 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              2. Acceptation des conditions
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              L'utilisation du Service implique l'acceptation pleine et entière des présentes
              Conditions Générales d'Utilisation (CGU). En créant un compte et en utilisant le
              Service, vous reconnaissez avoir pris connaissance de ces CGU et vous engagez à les
              respecter.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Service.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 3 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              3. Inscription et compte utilisateur
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              3.1. Création de compte
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Pour utiliser le Service, vous devez créer un compte en fournissant des informations
              exactes et à jour. Vous êtes responsable de la confidentialité de vos identifiants
              de connexion.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              3.2. Identifiants ANEF
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Pour bénéficier du suivi automatique, vous devez renseigner vos identifiants ANEF
              (nom d'utilisateur et mot de passe). Ces identifiants sont stockés de manière
              sécurisée et chiffrée et sont utilisés uniquement pour :
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primary="• Se connecter automatiquement à votre compte ANEF"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Récupérer l'état de vos dossiers"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Vous notifier des changements et demandes de compléments"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              3.3. Responsabilité de l'utilisateur
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Vous êtes entièrement responsable de toute activité effectuée via votre compte.
              En cas d'utilisation non autorisée de votre compte, vous devez immédiatement nous
              en informer via WhatsApp au +262 639 77 86 00.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 4 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              4. Protection des données personnelles
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Vos données personnelles sont traitées conformément au Règlement Général sur la
              Protection des Données (RGPD). Les données collectées incluent :
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primary="• Nom, prénom, email, numéro de téléphone"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Identifiants ANEF (stockés de manière chiffrée)"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Informations relatives à vos dossiers ANEF"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Données de connexion et d'utilisation du Service"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              4.1. Finalité du traitement
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Vos données sont collectées et traitées uniquement pour assurer le fonctionnement
              du Service, notamment la synchronisation automatique avec l'ANEF et l'envoi de
              notifications.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              4.2. Vos droits
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de
              suppression et de portabilité de vos données. Pour exercer ces droits, contactez-nous
              via WhatsApp au +262 639 77 86 00.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              4.3. Sécurité des données
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées
              pour protéger vos données contre tout accès non autorisé, perte ou divulgation.
              Vos identifiants ANEF sont chiffrés à l'aide d'algorithmes robustes.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 5 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              5. Fonctionnement du service
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              5.1. Synchronisation ANEF
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Service se connecte périodiquement à votre compte ANEF pour vérifier l'état de
              vos dossiers. La fréquence de synchronisation peut varier mais est généralement
              effectuée plusieurs fois par jour.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              5.2. Notifications
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Lorsqu'un changement est détecté sur vos dossiers ANEF (notamment une demande de
              complément), vous recevez une notification push sur votre appareil mobile si vous
              avez installé l'application PWA et autorisé les notifications.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              5.3. Limitations
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Service dépend de la disponibilité et du fonctionnement de la plateforme ANEF.
              Nous ne pouvons garantir une disponibilité à 100% du Service en raison de :
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primary="• Maintenances ou dysfonctionnements de l'ANEF"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Modifications de l'interface ou de l'API ANEF"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Problèmes techniques ou de réseau"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 6 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              6. Tarification et gratuité
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Service est actuellement proposé <strong>gratuitement à 100%</strong> dans le
              cadre d'une offre de lancement.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Cyber Mangaza se réserve le droit de faire évoluer cette offre dans le futur et
              d'introduire des options payantes pour des fonctionnalités premium. Les utilisateurs
              seront prévenus au moins 30 jours à l'avance de tout changement tarifaire.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Les fonctionnalités de base du Service (suivi ANEF et notifications) resteront
              accessibles gratuitement ou à un tarif abordable.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 7 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              7. Responsabilités et garanties
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              7.1. Service "en l'état"
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Service est fourni "en l'état" sans garantie d'aucune sorte. Le Cyber Mangaza
              s'efforce de fournir un service fiable mais ne peut garantir que le Service sera
              exempt d'erreurs ou disponible en permanence.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              7.2. Limitation de responsabilité
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Cyber Mangaza ne peut être tenu responsable :
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primary="• Des décisions prises par l'administration française concernant vos dossiers ANEF"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• De la clôture de vos dossiers ANEF si vous ne répondez pas aux demandes de compléments"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Des retards ou défaillances de synchronisation dus à l'ANEF"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• De la non-réception de notifications en cas de problème technique sur votre appareil"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              7.3. Obligation de l'utilisateur
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Vous restez entièrement responsable de vos dossiers ANEF. Le Service est un outil
              d'aide et de notification, mais ne remplace pas votre obligation de consulter
              régulièrement votre compte ANEF et de répondre dans les délais aux demandes de
              l'administration.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 8 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              8. Utilisation acceptable
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Vous vous engagez à utiliser le Service de manière appropriée et à ne pas :
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <ListItemText
                  primary="• Tenter de contourner les mesures de sécurité du Service"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Utiliser le Service pour des activités illégales ou frauduleuses"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Partager vos identifiants de compte avec des tiers"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Surcharger ou perturber le fonctionnement du Service"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Utiliser le Service pour surveiller les comptes ANEF de tiers sans autorisation"
                  primaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mt: 2 }}>
              Toute violation de ces règles peut entraîner la suspension ou la suppression de
              votre compte sans préavis.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 9 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              9. Résiliation
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              9.1. Résiliation par l'utilisateur
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Vous pouvez à tout moment supprimer votre compte depuis les paramètres de
              l'application. La suppression est immédiate et entraîne la suppression définitive
              de toutes vos données, y compris vos identifiants ANEF.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              9.2. Résiliation par le Cyber Mangaza
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Cyber Mangaza se réserve le droit de suspendre ou supprimer votre compte en cas
              de violation des présentes CGU, notamment en cas d'utilisation abusive du Service.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 10 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              10. Modifications des CGU
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Le Cyber Mangaza se réserve le droit de modifier les présentes CGU à tout moment.
              Les utilisateurs seront informés de toute modification substantielle par notification
              ou email. L'utilisation continue du Service après modification des CGU vaut
              acceptation des nouvelles conditions.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 11 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              11. Propriété intellectuelle
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Tous les éléments du Service (code source, design, logos, textes, etc.) sont la
              propriété exclusive du Cyber Mangaza et sont protégés par les lois sur la propriété
              intellectuelle. Toute reproduction, distribution ou utilisation non autorisée est
              strictement interdite.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 12 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              12. Droit applicable et juridiction
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Les présentes CGU sont régies par le droit français. En cas de litige, les parties
              s'efforceront de trouver une solution amiable. À défaut, les tribunaux français
              seront seuls compétents.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article 13 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              13. Contact
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Pour toute question concernant ces Conditions Générales d'Utilisation ou le Service,
              vous pouvez nous contacter :
            </Typography>
            <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50', mt: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Cyber Mangaza
              </Typography>
              <Typography variant="body2" paragraph>
                📍 Adresse : Combani, en face de la pharmacie, Mayotte
              </Typography>
              <Typography variant="body2" paragraph>
                📱 WhatsApp : +262 639 77 86 00
              </Typography>
              <Button
                variant="contained"
                color="success"
                href="https://wa.me/262639778600"
                target="_blank"
                sx={{ mt: 1 }}
              >
                Nous contacter sur WhatsApp
              </Button>
            </Paper>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Acceptation */}
          <Box sx={{ bgcolor: 'info.light', p: 3, borderRadius: 2, mt: 4 }}>
            <Typography variant="body1" sx={{ color: 'white', fontWeight: 500, textAlign: 'center' }}>
              En utilisant le Service, vous reconnaissez avoir lu, compris et accepté l'intégralité
              des présentes Conditions Générales d'Utilisation.
            </Typography>
          </Box>

          {/* Boutons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(-1)}
            >
              Retour
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
            >
              Retour à l'accueil
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.100', py: 3, mt: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center" color="text.secondary">
            © 2025 Cyber Mangaza - Système de Suivi ANEF
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsOfService;
